import { Application, Container } from 'pixi.js';
import { forgetFilterStackInputs } from './filterStack';
import { BASE } from './palette';

/** 创建好的渲染器句柄 */
export interface RendererHandle {
  readonly app: Application;
  /** 渲染器本体。要把东西画进 RenderTexture（卡面 FBO）时用它 */
  readonly renderer: Application['renderer'];
  /**
   * **逻辑世界**。所有界面都往这里加，坐标一律按逻辑画布算，
   * 缩放和平移由这里统一处理 —— 界面代码永远不需要知道窗口多大。
   */
  readonly world: Container;
  destroy(): void;
}

/** 逻辑画布尺寸。窗口比它大就放大，比它小就缩小，长宽比永远不变 */
export const LOGICAL_WIDTH = 1280;
export const LOGICAL_HEIGHT = 800;

/**
 * 启动 PixiJS。
 *
 * ⚠️ 这里的每一条设置都对应 `old_repo` 踩过的坑（见 `docs/archive/old-repo-review.md` §1）：
 * - `preference: 'webgl'` —— 明确不用 WebGPU，V1 只支持 WebGL2（`docs/tech-stack.md` §2）
 * - `autoDensity` + `resolution` —— 否则高分屏发虚
 * - `resizeTo: window` —— **全项目唯一**的尺寸来源，不许再有第二个 resize 监听
 * - 渲染循环只有**这一个** ticker 回调
 *
 * 这个函数在 React 之外调用（`src/main.ts`），所以不存在 StrictMode 的重复挂载问题。
 */
export async function createRenderer(host: HTMLElement): Promise<RendererHandle> {
  const app = new Application();

  await app.init({
    preference: 'webgl',
    autoDensity: true,
    resolution: window.devicePixelRatio,
    resizeTo: window,
    // ⚠️ **纯黑**，不是"很深的灰"。这个颜色是**信纸箱/桌面**的颜色 ——
    //    逻辑画布长宽比固定（1280×800），窗口不是 16:10 时会在两边
    //    （宽窗是左右、高窗是上下）留出这一圈"舞台之外"。
    //    第一版这里是 `0x0c0f14`（色板定稿之前的深蓝），后果是：
    //    留白处的颜色和游戏自己的黑**不是同一个黑**，于是那一圈在视觉上
    //    变成"没画完的空白"而不是"纸外边"。色板纪律见 `palette.ts` 的 `BASE.void`
    background: BASE.void,
    antialias: true,
  });

  host.appendChild(app.canvas);

  /**
   * **吃掉浏览器默认的右键 / 长按菜单。**
   *
   * `touch-action: none` 挡得住滚动与缩放，但**光标右键菜单**、以及部分浏览器
   * 的长按菜单仍会弹出来。游戏里没有「网页菜单」这回事，一律 `preventDefault`。
   */
  app.canvas.addEventListener('contextmenu', (event) => event.preventDefault());

  const world = new Container();
  app.stage.addChild(world);

  // ⚠️ 不监听 window 的 resize 事件 —— `resizeTo` 已经处理了 canvas 尺寸，
  // 我们只在每帧按当前 screen 尺寸重算缩放。这样就不存在"两处各算一次"的经典错位。
  app.ticker.add(() => {
    const scale = Math.min(app.screen.width / LOGICAL_WIDTH, app.screen.height / LOGICAL_HEIGHT);
    world.scale.set(scale);
    world.position.set(
      Math.round((app.screen.width - LOGICAL_WIDTH * scale) / 2),
      Math.round((app.screen.height - LOGICAL_HEIGHT * scale) / 2),
    );
  });

  /**
   * ★ **窗口一变就忘了滤镜栈里的旧贴图。**
   *
   * resize 会把旧尺寸的池贴图销毁，而 Pixi 的 `FilterSystem` 跨帧复用 slot、
   * 还留着它们的引用 —— 下一次嵌套 push 会读 `null.source._resolution` 并整屏黑。
   * 病根、后果与"为什么要绕"都在 `filterStack.ts`。
   */
  app.renderer.on('resize', () => {
    forgetFilterStackInputs(app.renderer.filter);
  });

  // dev 下把 app 挂到 window：既方便 @pixi/devtools，也方便冒烟测试断言渲染器类型
  if (import.meta.env.DEV) {
    (globalThis as unknown as { __PIXI_APP__?: Application }).__PIXI_APP__ = app;
  }

  return {
    app,
    world,
    renderer: app.renderer,
    destroy() {
      app.destroy(true, { children: true });
    },
  };
}
